<template>
  <app-page-container :custom="1600" :min-height="0">
    <app-page-header design="new" :title="`Import #${importId}`" compact />

    <section class="ui-page">
    <app-content-skeleton v-if="loadingImport" layout="block" label="Loading import" />
    <app-error-state v-else-if="importError" :message="importError" @retry="loadImport" />

    <template v-else-if="crewImport">
      <app-card design="new" class="import-detail__summary">
        <template #header>
          <div class="import-detail__title">
            <h2>Import status</h2>
            <import-status-badge :status="crewImport.status" />
          </div>
        </template>

        <div class="import-detail__panel" :class="`import-detail__panel--${statusTone}`">
          <dl class="import-detail__meta">
            <div><dt>File</dt><dd>{{ crewImport.file_name }}</dd></div>
            <div><dt>Mode</dt><dd>{{ modeLabel }}</dd></div>
            <div><dt>Created</dt><dd>{{ formatDateTime(crewImport.created_at) }} · {{ userName(crewImport.created_by) }}</dd></div>
            <div v-if="crewImport.applied_at">
              <dt>Applied</dt><dd>{{ formatDateTime(crewImport.applied_at) }} · {{ userName(crewImport.applied_by) }}</dd>
            </div>
          </dl>
          <div class="import-detail__actions">
            <app-button v-if="canContinue" design="new" @click.native="continueMapping">Continue mapping</app-button>
            <app-button v-if="canRetry" ref="retryButton" design="new" @click.native="retry">Retry preview</app-button>
            <app-button v-if="canApply" ref="applyButton" design="new" @click.native="apply">
              Apply {{ formatNumber(crewImport.selected_count) }} {{ crewImport.selected_count === 1 ? 'row' : 'rows' }}
            </app-button>
            <app-button v-if="canCancel" ref="cancelButton" design="new" theme="dark" @click.native="cancel">Cancel import</app-button>
          </div>
        </div>

        <div v-if="isActive" class="import-detail__progress" role="status">
          <div class="import-detail__progress-text">
            <strong>{{ crewImport.phase || 'Working' }}</strong>
            <span>{{ progressText }}</span>
          </div>
          <div
            v-if="!isReading"
            class="import-detail__bar"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuenow="progressPercent"
            :aria-label="`${progressPercent}% of the rows processed`"
          >
            <span :style="{ width: `${progressPercent}%` }" />
          </div>
        </div>

        <div v-if="!isReading" class="import-detail__metrics">
          <app-metric-card
            v-for="metric in metrics"
            :key="metric.label"
            design="new"
            padding="compact"
            :label="metric.label"
            :value="metric.value"
            :tone="metric.tone"
            :meta="metric.meta"
          />
        </div>
      </app-card>

      <div v-if="crewImport.blocking_errors.length" class="import-detail__notice import-detail__notice--red" role="alert">
        <strong>This import cannot be applied</strong>
        <ul>
          <li v-for="item in crewImport.blocking_errors" :key="`${item.code}-${item.key || ''}`">{{ item.message }}</li>
        </ul>
        <span>Fix the file and upload it again.</span>
      </div>
      <div v-if="crewImport.error_message" class="import-detail__notice import-detail__notice--red" role="alert">
        <strong>{{ crewImport.error_message }}</strong>
      </div>
      <div v-for="warning in crewImport.warnings" :key="warning.code" class="import-detail__notice import-detail__notice--amber">
        {{ warning.message }}
      </div>
      <div v-if="showNotInFile" class="import-detail__notice import-detail__notice--blue">
        {{ formatNumber(crewImport.not_in_file_count) }} crew are not in this file. Applying marks them as outside the latest import; nobody is deleted or deactivated.
      </div>

      <template v-if="showRows">
        <app-filter-toolbar controls-align="start">
          <template #primary><app-input id="import-row-search" v-model="search" design="new" type="search" aria-label="Search" placeholder="# or name" /></template>
          <app-checkbox v-model="selectedOnly" design="new" label="Only rows selected for apply" />
          <template #selection><app-filters v-model="action" design="new" refined weight="medium" name="crew-import-row-action" aria-label="Row action" :filters="actionFilters" /></template>
          <template v-if="action !== 'all' || search || selectedOnly" #clear><app-clear-filters-button design="new" icon-only @clear="clearFilters" /></template>
        </app-filter-toolbar>
        <app-content-skeleton v-if="rowsLoading" label="Loading import rows" :columns="columns" min-width="1200px" />
        <app-error-state v-else-if="rowsError" :message="rowsError" @retry="loadRows" />
        <app-card v-else-if="rows.length" design="new" variant="table" header-density="compact">
        <app-data-table
          design="new"
          density="dense"
          :columns="columns"
          :rows="rows"
          :loading="rowsLoading"
          min-width="1200px"
        >
          <template #cell-selected="{ row }">
            <span v-if="canToggle(row)" class="import-detail__select">
              <app-checkbox
                :value="row.selected"
                design="new"
                single
                :label="`Apply row ${row.row_number}`"
                @change="toggle(row, $event)"
              />
            </span>
            <!-- An empty slot would make AppDataTable print the raw value ("false"). -->
            <span v-else class="import-detail__muted" aria-hidden="true">—</span>
          </template>
          <template #cell-row_number="{ row }"><app-table-text :text="row.row_number" /></template>
          <template #cell-external_key="{ row }"><app-table-text :text="row.external_key" /></template>
          <template #cell-crew="{ row }">
            <app-table-user :name="row.target ? row.target.full_name : newCrewName(row)"><router-link v-if="row.target" class="ui-link" :to="profileLink(row.target)">{{ row.target.full_name }}</router-link><template v-else>{{ newCrewName(row) }}</template></app-table-user>
          </template>
          <template #cell-action="{ row }">
            <app-status-badge design="new" compact :tone="actionMeta(row).tone" :label="actionMeta(row).label" />
          </template>
          <template #cell-changes="{ row }">
            <span v-if="row.action === 'error'" class="import-detail__muted">Not applied</span>
            <span v-else-if="!hasChanges(row)" class="import-detail__muted">No changes</span>
            <div v-else class="import-detail__changes">
              <span>{{ changeCountText(row.changes) }}</span>
              <app-icon-button :label="`Show diff of row ${row.row_number}`" @click="showDiff(row)"><i class="pi pi-eye" aria-hidden="true" /></app-icon-button>
            </div>
          </template>
          <template #cell-issues="{ row }">
            <ul v-if="row.errors.length || row.warnings.length" class="import-detail__issues">
              <li v-for="(item, index) in row.errors" :key="`e${index}`" class="import-detail__issue--error"><app-table-text :text="item.message" /></li>
              <li v-for="(item, index) in row.warnings" :key="`w${index}`" class="import-detail__issue--warning"><app-table-text :text="item.message" /></li>
            </ul>
            <span v-else class="import-detail__muted">—</span>
          </template>
          <template #cell-result="{ row }">
            <template v-if="showResult">
              <app-status-badge design="new" compact :tone="applyMeta(row).tone" :label="applyMeta(row).label" />
              <app-table-text v-if="row.apply_error" :text="row.apply_error" />
            </template>
            <span v-else class="import-detail__muted">—</span>
          </template>
        </app-data-table>
        </app-card>
        <app-no-results v-else design="new" title="No rows" text="" />
        <app-pagination v-if="!rowsLoading && !rowsError && rowsCount" design="new" placement="embedded" :page="page" :pages="pages" :count="rowsCount" :visible-count="rows.length" :page-size="pageSize" :loading="rowsLoading" @change="setPage" />
      </template>
    </template>

    </section>
    <crew-import-modal />
    <import-row-diff-modal :labels="labels" />
  </app-page-container>
</template>

<script>
import {
  getClimbingMountainCrewImport,
  getClimbingMountainCrewImportFields,
  getClimbingMountainCrewImportRows,
  patchClimbingMountainCrewImportRow,
  postClimbingMountainCrewImportApply,
  postClimbingMountainCrewImportCancel,
  postClimbingMountainCrewImportStart
} from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import CrewImportModal from './components/CrewImportModal'
import ImportRowDiffModal from './components/ImportRowDiffModal'
import ImportStatusBadge from './components/ImportStatusBadge'
import {
  ACTIVE_STATUSES,
  apiErrorMessage,
  CANCELLABLE_STATUSES,
  changeCountText,
  EDITABLE_STATUSES,
  formatDateTime,
  formatNumber,
  IMPORT_MODE,
  ROW_ACTION,
  ROW_APPLY_STATUS,
  statusMeta,
  userName
} from './importState'

const PAGE_SIZE = 40
const POLL_INTERVAL = 2000
const STATUSES_WITH_ROWS = ['diff_ready', 'blocked', 'applying', 'applied', 'applied_with_errors', 'failed']

export default {
  name: 'ClimbingMountainCrewImportDetail',
  components: { CrewImportModal, ImportRowDiffModal, ImportStatusBadge, AppContentSkeleton, AppErrorState, AppTableText, AppTableUser },
  data() {
    return {
      crewImport: null,
      loadingImport: true,
      importError: '',
      labels: {},
      rows: [],
      rowsCount: 0,
      pages: 1,
      page: 1,
      pageSize: PAGE_SIZE,
      rowsLoading: false,
      rowsError: '',
      rowsRequest: 0,
      action: 'all',
      search: '',
      selectedOnly: false,
      searchTimer: null,
      pollTimer: null,
      columns: [
        { key: 'selected', label: 'Apply', width: '72px' },
        { key: 'row_number', label: 'Row', width: '70px' },
        { key: 'external_key', label: '#', width: '100px' },
        { key: 'crew', label: 'Crew', minWidth: '180px' },
        { key: 'action', label: 'Action', width: '120px' },
        { key: 'changes', label: 'Changes', width: '210px' },
        { key: 'issues', label: 'Issues', minWidth: '240px' },
        { key: 'result', label: 'Result', width: '150px' }
      ]
    }
  },
  computed: {
    importId() { return this.$route.params.importId },
    statusTone() { return this.crewImport ? statusMeta(this.crewImport.status).tone : 'neutral' },
    modeLabel() { return this.crewImport ? IMPORT_MODE[this.crewImport.mode] : '' },
    isActive() { return this.crewImport && ACTIVE_STATUSES.includes(this.crewImport.status) },
    isReading() { return this.crewImport && this.crewImport.status === 'parsing' },
    canContinue() { return this.crewImport && EDITABLE_STATUSES.includes(this.crewImport.status) },
    canRetry() {
      // a file that could not be read has no preview to retry: it has to be uploaded again
      return this.crewImport && this.crewImport.status === 'failed'
        && Boolean(this.crewImport.diff_started_at) && !this.crewImport.apply_started_at
    },
    canApply() { return this.crewImport && this.crewImport.status === 'diff_ready' },
    canCancel() { return this.crewImport && CANCELLABLE_STATUSES.includes(this.crewImport.status) },
    showRows() { return this.crewImport && STATUSES_WITH_ROWS.includes(this.crewImport.status) && this.crewImport.total_rows > 0 },
    showResult() {
      return this.crewImport && ['applying', 'applied', 'applied_with_errors', 'failed'].includes(this.crewImport.status)
    },
    showNotInFile() {
      return this.crewImport && this.crewImport.mode === 'full' && this.crewImport.not_in_file_count > 0
        && ['diff_ready', 'applying'].includes(this.crewImport.status)
    },
    progressPercent() {
      const total = this.crewImport ? this.crewImport.total_rows : 0
      if (!total) return 0
      return Math.min(100, Math.round((this.crewImport.processed_rows / total) * 100))
    },
    progressText() {
      if (this.isReading) return 'The file is being read. This page updates by itself.'
      const { processed_rows: processed, total_rows: total } = this.crewImport
      return total ? `${formatNumber(processed)} of ${formatNumber(total)} rows` : 'Preparing'
    },
    metrics() {
      const item = this.crewImport
      const list = [
        { label: 'Rows in file', value: formatNumber(item.total_rows), tone: 'neutral' },
        { label: 'To create', value: formatNumber(item.created_count), tone: 'green' },
        { label: 'To update', value: formatNumber(item.updated_count), tone: 'blue' },
        { label: 'Unchanged', value: formatNumber(item.unchanged_count), tone: 'neutral' },
        { label: 'With errors', value: formatNumber(item.error_count), tone: item.error_count ? 'red' : 'neutral', meta: item.error_count ? 'Skipped on apply' : '' },
        { label: 'Selected for apply', value: formatNumber(item.selected_count), tone: 'blue' }
      ]
      if (this.showResult && item.status !== 'applying') {
        list.push(
          { label: 'Applied', value: formatNumber(item.applied_count), tone: 'green' },
          { label: 'Failed', value: formatNumber(item.failed_count), tone: item.failed_count ? 'red' : 'neutral' },
          { label: 'Conflicts', value: formatNumber(item.conflict_count), tone: item.conflict_count ? 'amber' : 'neutral', meta: item.conflict_count ? 'Changed after the preview' : '' }
        )
      }
      return list
    },
    actionFilters() {
      const item = this.crewImport
      const count = value => (this.isActive && item.status === 'diff_building' ? '…' : formatNumber(value))
      return [
        { value: 'all', label: `All rows (${count(item.total_rows)})` },
        { value: 'create', label: `Create (${count(item.created_count)})`, tone: 'green' },
        { value: 'update', label: `Update (${count(item.updated_count)})` },
        { value: 'unchanged', label: `Unchanged (${count(item.unchanged_count)})` },
        { value: 'error', label: `Errors (${count(item.error_count)})`, tone: 'red' }
      ]
    }
  },
  watch: {
    action() { this.onFilterChange() },
    selectedOnly() { this.onFilterChange() },
    search() { this.onFilterChange(300) },
    importId() { this.reload() }
  },
  created() {
    getClimbingMountainCrewImportFields().then(response => {
      const labels = {}
      response.data.importable.forEach(field => { labels[field.key] = field.label })
      this.labels = labels
    }).catch(() => {})
    this.loadImport()
  },
  beforeDestroy() {
    window.clearTimeout(this.pollTimer)
    window.clearTimeout(this.searchTimer)
  },
  methods: {
    formatDateTime,
    formatNumber,
    userName,
    reload() {
      this.crewImport = null
      this.rows = []
      this.page = 1
      this.loadingImport = true
      this.loadImport()
    },
    loadImport() {
      window.clearTimeout(this.pollTimer)
      this.importError = ''
      if (!this.crewImport) this.loadingImport = true
      const previous = this.crewImport && this.crewImport.status
      return getClimbingMountainCrewImport(this.importId).then(response => {
        this.crewImport = response.data
        this.afterImportLoaded(previous)
      }).catch(() => {
        if (!this.crewImport) this.importError = 'The import could not be loaded.'
      }).finally(() => { this.loadingImport = false })
    },
    afterImportLoaded(previous) {
      const status = this.crewImport.status
      if (ACTIVE_STATUSES.includes(status)) {
        this.pollTimer = window.setTimeout(this.loadImport, POLL_INTERVAL)
        if (status === 'applying' && previous !== 'applying') this.loadRows()
        return
      }
      const justFinished = previous && previous !== status
      if (STATUSES_WITH_ROWS.includes(status) && (justFinished || !previous)) this.loadRows()
    },
    onFilterChange(delay = 0) {
      window.clearTimeout(this.searchTimer)
      this.searchTimer = window.setTimeout(() => {
        this.page = 1
        this.loadRows()
      }, delay)
    },
    setPage(page) {
      this.page = page
      this.loadRows()
    },
    clearFilters() {
      this.action = 'all'
      this.search = ''
      this.selectedOnly = false
    },
    loadRows() {
      const request = ++this.rowsRequest
      this.rowsLoading = true
      this.rowsError = ''
      getClimbingMountainCrewImportRows(this.importId, {
        page: this.page,
        page_size: this.pageSize,
        action: this.action === 'all' ? undefined : this.action,
        search: this.search.trim() || undefined,
        selected: this.selectedOnly ? true : undefined
      }).then(response => {
        if (request !== this.rowsRequest) return
        this.rows = response.data.results
        this.rowsCount = response.data.count
        this.pages = response.data.pages
      }).catch(() => {
        if (request === this.rowsRequest) this.rowsError = 'The rows could not be loaded.'
      }).finally(() => {
        if (request === this.rowsRequest) this.rowsLoading = false
      })
    },
    canToggle(row) {
      return this.crewImport.status === 'diff_ready' && ['create', 'update'].includes(row.action)
    },
    toggle(row, selected) {
      patchClimbingMountainCrewImportRow(this.importId, row.id, { selected }).then(response => {
        row.selected = response.data.selected
        return this.loadImport()
      }).catch(error => {
        this.$notify({ type: 'error', title: apiErrorMessage(error) })
        this.loadRows()
      })
    },
    actionMeta(row) { return ROW_ACTION[row.action] || { label: row.action, tone: 'neutral' } },
    applyMeta(row) { return ROW_APPLY_STATUS[row.apply_status] || { label: row.apply_status, tone: 'neutral' } },
    profileLink(target) {
      return { name: 'climbing-mountain-crew-profile', params: { id: target.id } }
    },
    newCrewName(row) {
      const change = key => (row.changes[key] ? row.changes[key].new : '')
      return [change('first_name'), change('last_name')].filter(Boolean).join(' ') || 'New crew'
    },
    changeCountText,
    hasChanges(row) { return Object.keys(row.changes || {}).length > 0 },
    showDiff(row) {
      this.$modal.show('crew-import-row-diff', { row })
    },
    continueMapping() {
      this.$modal.show('crew-import', { importId: this.crewImport.id })
    },
    run(buttonName, request, successTitle) {
      const button = this.$refs[buttonName]
      if (button) button.preload = true
      return request(this.importId).then(response => {
        this.crewImport = response.data
        if (successTitle) this.$notify({ type: 'success', title: successTitle })
        this.afterImportLoaded(null)
        return true
      }).catch(error => {
        this.$notify({ type: 'error', title: apiErrorMessage(error) })
        return false
      }).finally(() => { if (button) button.preload = false })
    },
    retry() {
      this.run('retryButton', postClimbingMountainCrewImportStart, 'The preview is being built')
    },
    apply() {
      this.run('applyButton', postClimbingMountainCrewImportApply, 'The selected rows are being applied')
    },
    cancel() {
      this.run('cancelButton', postClimbingMountainCrewImportCancel, 'The import was cancelled')
    }
  }
}
</script>

<style lang="scss" scoped>
.import-detail {
  color: $ui-text;
  font-family: $ui-font-body;

  &__back {
    color: $ui-muted;
    font-size: $ui-font-size-control;
    font-weight: 600;
    text-decoration: none;

    &:hover { color: $ui-blue; }
    &:focus-visible { outline: 2px solid $ui-blue; outline-offset: 2px; }
  }

  &__skeletons { display: flex; flex-direction: column; gap: $ui-space-16; }
  &__skeleton { display: block; height: 96px; border-radius: $ui-control-radius; background-color: $ui-line; }

  &__error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $ui-space-16;
    padding: $ui-space-16 $ui-space-20;
    border: 1px solid $ui-red;
    border-radius: $ui-panel-radius;
    background-color: $ui-red-soft;
    color: $ui-red;
    font-size: $ui-font-size-body;

    &--flat { border-width: 0 0 1px; border-radius: 0; }
  }

  &__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $ui-space-12;

    h2 {
      margin: 0;
      color: $ui-ink;
      font-family: $ui-font-heading;
      font-size: $ui-font-size-section-title;
      font-weight: 600;
    }
  }

  &__panel {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: $ui-space-16;
    padding: $ui-space-16;
    border: 1px solid $ui-line;
    border-radius: $ui-panel-radius;
    background-color: $ui-surface-soft;

    &--blue { background-color: $ui-blue-soft; }
    &--green { background-color: $ui-green-soft; }
    &--amber { background-color: $ui-amber-soft; }
    &--red { background-color: $ui-red-soft; }
  }

  &__meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: $ui-space-12 $ui-space-24;
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;

    dt { color: $ui-muted; font-size: $ui-font-size-metadata; font-weight: 600; }
    dd { margin: $ui-space-4 0 0; color: $ui-ink; font-size: $ui-font-size-body; font-weight: 600; overflow-wrap: anywhere; }
  }

  &__actions {
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: $ui-space-12;
  }

  &__progress { margin-top: $ui-space-16; }

  &__progress-text {
    display: flex;
    justify-content: space-between;
    gap: $ui-space-12;
    margin-bottom: $ui-space-8;
    font-size: $ui-font-size-body;

    strong { color: $ui-ink; font-weight: 600; }
    span { color: $ui-muted; }
  }

  &__bar {
    height: 8px;
    overflow: hidden;
    border-radius: $ui-control-radius;
    background-color: $ui-line;

    span { display: block; height: 100%; background-color: $ui-blue; transition: width .3s ease; }
  }

  &__metrics {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: $ui-space-16;
    margin-top: $ui-space-16;
  }

  &__notice {
    display: flex;
    flex-direction: column;
    gap: $ui-space-8;
    padding: $ui-space-12 $ui-space-16;
    border: 1px solid $ui-line;
    border-radius: $ui-panel-radius;
    font-size: $ui-font-size-body;
    line-height: 1.45;

    ul { margin: 0; padding-left: $ui-space-20; }
    &--red { border-color: $ui-red; background-color: $ui-red-soft; color: $ui-red; }
    &--amber { border-color: $ui-amber; background-color: $ui-amber-soft; color: $ui-amber; }
    &--blue { background-color: $ui-blue-soft; color: $ui-text; }
  }

  &__rows-head {
    display: flex;
    flex-direction: column;
    gap: $ui-space-16;
    padding: $ui-space-16 $ui-space-20;
    border-bottom: 1px solid $ui-line;
  }

  &__segments { align-self: flex-start; max-width: 100%; overflow-x: auto; }

  &__row-fields {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) auto auto;
    align-items: end;
    gap: $ui-space-16;
  }

  &__link {
    color: $ui-blue;
    font-weight: 600;
    text-decoration: none;

    &:hover { color: $ui-blue-hover; text-decoration: underline; }
    &:focus-visible { outline: 2px solid $ui-blue; outline-offset: 2px; }
  }

  // The row number stays as the accessible name of the checkbox but is not shown.
  &__select ::v-deep .checkbox__text {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  &__muted { color: $ui-muted; font-size: $ui-font-size-metadata; }

  &__changes {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $ui-space-8 $ui-space-12;
    color: $ui-ink;
    font-weight: 600;
  }
  &__result-note { display: block; margin-top: $ui-space-4; }

  &__issues {
    display: flex;
    flex-direction: column;
    gap: $ui-space-4;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: $ui-font-size-body;
  }

  &__issue--error { color: $ui-red; }
  &__issue--warning { color: $ui-amber; }
}

@media (max-width: 1180px) {
  .import-detail__row-fields { grid-template-columns: minmax(0, 1fr); align-items: stretch; }
}

@media (max-width: 760px) {
  .import-detail__panel { flex-direction: column; }
  .import-detail__meta { grid-template-columns: minmax(0, 1fr); }
  .import-detail__actions { justify-content: flex-start; }
}
</style>
